// Derivation for the live2 board.
//
// The writers deliberately store only facts: planned times, observed times, and
// published occupancy. Status, current stop, delay and the AWAITING/MISSING
// distinction are all DERIVED HERE, client-side, for two reasons — they are
// functions of wall-clock time (so no Firestore event fires when they change),
// and keeping them out of the documents meant no writer redeploy to change them.
//
// One helper, used by both Sidebar2 and LiveView2, so the two views cannot drift.
// If the validation phase later needs the same definitions server-side, this file
// is the specification to port.

import { OccupancyState } from '../components/icons/Occupancy';
import { hhmm, toMs } from '../utils/liveTime';
import type {
  JourneyDoc,
  JourneyStatus,
  JourneyStopDoc,
  OccErrorVsH1,
  OccSlot,
  OccStopEntry,
  OccupancyDoc,
  OccupancyStateName,
} from './types';

/**
 * How long after a stop is called we still expect the VU count.
 * VU_DELAY_SECONDS is 35 in trip-journeyid-publish-v2, plus handler time, so
 * this must comfortably exceed it or every stop would flash MISSING on the way
 * to MEASURED.
 */
export const COUNT_GRACE_MS = 120 * 1000;

/**
 * How long before scheduled departure a train joins the board.
 *
 * Without a lower bound the board shows everything RIS:Journeys knows about, which
 * is up to 48 hours of timetable — dozens per line. `expires_at` alone is not a
 * live filter: it is terminus arrival plus a tail, which for a journey scheduled
 * tomorrow is comfortably in the future.
 */
export const PRE_DEPARTURE_LEAD_MS = 10 * 60 * 1000;

/** Re-evaluate time-derived state often enough that transitions look prompt. */
export const CLOCK_TICK_MS = 10 * 1000;

export function levelToState(level?: string | null): OccupancyState | null {
  switch ((level ?? '').toLowerCase()) {
    case 'low': return OccupancyState.low;
    case 'medium': return OccupancyState.medium;
    case 'high': return OccupancyState.high;
    default: return null;
  }
}

/**
 * Per-unit, per-carriage levels as the nested array <Carriage> expects.
 * Units are ordered by unit_index, carriages by carriage_index. Replaces
 * utils/normalizeCars.ts, which decoded the aggregator's {group, cars:{car_1}}
 * shape — unrelated to this one.
 */
export function slotToCars(slot?: OccSlot | null): OccupancyState[][] {
  const units = slot?.units ?? [];
  if (!units.length) return [];
  return [...units]
    .sort((a, b) => (a.unit_index ?? 0) - (b.unit_index ?? 0))
    .map((u) =>
      [...(u.carriages ?? [])]
        .sort((a, b) => (a.carriage_index ?? 0) - (b.carriage_index ?? 0))
        .map((c) => levelToState(c.level) ?? OccupancyState.low)
    )
    .filter((cars) => cars.length > 0);
}

export type DerivedTime = {
  schedLabel: string | null;
  obsLabel: string | null;
  schedMs: number | null;
  obsMs: number | null;
  delaySec: number | null;
};

export type DerivedOccupancy = {
  state: OccupancyStateName;
  /** Which slot the numbers came from — null for NONE. */
  slot: OccSlot | null;
  reason: string | null;
  level: OccupancyState | null;
  passengers: number | null;
  capacity: number | null;
  pct: number | null;
  cars: OccupancyState[][];
  /** FORECAST only */
  horizon: number | null;
  modelVersion: string | null;
  /** MEASURED only — measured minus the one-stop-out forecast */
  errorVsH1: OccErrorVsH1 | null;
  /**
   * MEASURED only — how close the count came to the n1 forecast, as
   * ceil(actual / forecast * 100). Forecast 100, actual 99 -> 99%.
   *
   * Not capped: an overshoot reads above 100%, which is informative rather than
   * wrong. Null when there is no forecast to compare against, or the forecast was
   * zero.
   */
  accuracyPct: number | null;
  /** The h1 forecast retained beside the measurement, for the past rows. */
  forecastH1: OccSlot | null;
};

export type DerivedStop = {
  seq: number;
  eva: string | null;
  name: string;
  cancelled: boolean;
  platform: string | null;
  arrival: DerivedTime;
  departure: DerivedTime;
  /** Any observed event at all — the stop was called. */
  called: boolean;
  calledAtMs: number | null;
  phase: 'PAST' | 'CURRENT' | 'FUTURE';
  isTerminus: boolean;
  occupancy: DerivedOccupancy;
};

export type DerivedJourney = {
  journeyId: string;
  routeId: string | null;
  lineCode: string;
  status: JourneyStatus;
  cancelled: boolean;
  originName: string | null;
  destinationName: string | null;
  originSchedLabel: string | null;
  destinationSchedLabel: string | null;
  currentSeq: number | null;
  nextSeq: number | null;
  delaySec: number | null;
  expiresMs: number | null;
  sourceUpdatedMs: number | null;
  stops: DerivedStop[];
  /** Counts for the header — how much of the VU output actually arrived. */
  coverage: { called: number; measured: number; awaiting: number; missing: number; total: number };
};

function derivedTime(t?: { scheduled?: any; observed?: any; delay_seconds?: number | null } | null): DerivedTime {
  return {
    schedLabel: hhmm(t?.scheduled),
    obsLabel: hhmm(t?.observed),
    schedMs: toMs(t?.scheduled),
    obsMs: toMs(t?.observed),
    delaySec: t?.delay_seconds ?? null,
  };
}

function sortedStops(doc: JourneyDoc | null): JourneyStopDoc[] {
  const stops = (doc?.stops ?? []).filter((s) => s && s.stop_sequence !== null && s.stop_sequence !== undefined);
  return [...stops].sort((a, b) => (a.stop_sequence ?? 0) - (b.stop_sequence ?? 0));
}

/**
 * The last stop with any observed event. This is "where the train is", and it is
 * the only progress signal available — the journey document carries no `progress`
 * or `status` field by design.
 */
function lastObservedSeq(stops: JourneyStopDoc[]): number | null {
  let seq: number | null = null;
  for (const s of stops) {
    if (toMs(s.departure?.observed) !== null || toMs(s.arrival?.observed) !== null) {
      seq = s.stop_sequence ?? seq;
    }
  }
  return seq;
}

/**
 * Cheap status for the sidebar: walks the stop array only, no occupancy needed.
 *
 * ARRIVED depends on the terminus arrival event being captured. If terminus
 * arrivals are lossy, a finished train stays ACTIVE until expires_at removes it
 * — which is the same known weakness the analysis-side stale review flagged, and
 * why expiry is enforced independently of status.
 */
export function deriveStatus(doc: JourneyDoc | null, nowMs: number): JourneyStatus {
  if (!doc) return 'NO_DATA';
  if (doc.cancelled) return 'CANCELLED';

  const stops = sortedStops(doc);
  const terminus = stops.length ? stops[stops.length - 1] : null;
  const terminusArrived =
    toMs(terminus?.arrival?.observed) !== null || toMs(doc.destination?.observed_arrival) !== null;
  if (terminusArrived) return 'ARRIVED';

  if (lastObservedSeq(stops) !== null) return 'ACTIVE';

  const schedDep =
    toMs(doc.origin?.scheduled_departure) ??
    (stops.length ? toMs(stops[0].departure?.scheduled) : null);
  if (schedDep !== null && schedDep <= nowMs) return 'NO_DATA';
  return 'SCHEDULED';
}

/**
 * Accuracy of the n1 forecast, as a percentage of the forecast that the count
 * actually reached. Rounded up. Forecast 100, actual 99 -> 99%.
 *
 * Not capped: an overshoot reads above 100%, which is informative rather than
 * wrong. Null when there is no forecast, or the forecast was zero — dividing by it
 * would give a meaningless number rather than an absent one.
 */
function accuracyPct(actual?: number | null, forecast?: number | null): number | null {
  if (actual === null || actual === undefined) return null;
  if (forecast === null || forecast === undefined || forecast === 0) return null;
  return Math.ceil((actual / forecast) * 100);
}

/**
 * Resolve one stop's occupancy to exactly one of five states.
 *
 * Order matters. A measurement always wins; an explicit MISSING written by the VU
 * controller (NO_UICS, PUBLISH_FAILED, …) beats a time-derived one because it
 * carries a reason; and a called stop within grace is AWAITING rather than
 * MISSING because the count is legitimately still in flight.
 *
 * State is never inferred from which fields happen to be populated — that
 * inference is what the explicit `state` field exists to replace.
 */
function deriveOccupancy(
  entry: OccStopEntry | undefined,
  called: boolean,
  calledAtMs: number | null,
  nowMs: number,
): DerivedOccupancy {
  const h1 = entry?.forecast_by_horizon?.['1'] ?? null;

  const fromSlot = (state: OccupancyStateName, slot: OccSlot | null, reason: string | null): DerivedOccupancy => ({
    state,
    slot,
    reason,
    level: levelToState(slot?.train?.level),
    passengers: slot?.train?.passengers ?? null,
    capacity: slot?.train?.capacity ?? null,
    pct: slot?.train?.pct ?? null,
    cars: slotToCars(slot),
    horizon: slot?.horizon ?? null,
    modelVersion: slot?.model_version ?? null,
    errorVsH1: slot?.error_vs_h1 ?? null,
    accuracyPct:
      state === 'MEASURED'
        ? accuracyPct(slot?.train?.passengers, h1?.train?.passengers)
        : null,
    forecastH1: h1,
  });

  if (entry?.ist) return fromSlot('MEASURED', entry.ist, null);

  // Explicit failure from the VU side — keeps any forecast for context.
  if (entry?.state === 'MISSING') {
    return fromSlot('MISSING', entry.forecast_latest ?? null, entry.reason ?? 'UNKNOWN');
  }

  if (called) {
    const elapsed = calledAtMs !== null ? nowMs - calledAtMs : null;
    const overdue = elapsed !== null && elapsed > COUNT_GRACE_MS;
    // No entry at all and past grace: the VU function never ran. Distinct from an
    // explicit MISSING, which at least tells us why.
    return fromSlot(
      overdue ? 'MISSING' : 'AWAITING',
      entry?.forecast_latest ?? null,
      overdue ? 'NO_VU_MESSAGE' : null,
    );
  }

  if (entry?.forecast_latest) return fromSlot('FORECAST', entry.forecast_latest, null);

  return fromSlot('NONE', null, null);
}

export function deriveJourney(
  journey: JourneyDoc | null,
  occupancy: OccupancyDoc | null,
  nowMs: number,
): DerivedJourney | null {
  if (!journey) return null;

  const stopDocs = sortedStops(journey);
  const currentSeq = lastObservedSeq(stopDocs);
  const occStops = occupancy?.stops ?? {};

  const coverage = { called: 0, measured: 0, awaiting: 0, missing: 0, total: stopDocs.length };

  const stops: DerivedStop[] = stopDocs.map((s, idx) => {
    const seq = s.stop_sequence as number;
    const arrival = derivedTime(s.arrival);
    const departure = derivedTime(s.departure);
    const calledAtMs = departure.obsMs ?? arrival.obsMs;
    const called = calledAtMs !== null;

    // Named `occ` rather than `occupancy` so it does not shadow the parameter.
    const occ = deriveOccupancy(occStops[String(seq)], called, calledAtMs, nowMs);

    if (called) coverage.called += 1;
    if (occ.state === 'MEASURED') coverage.measured += 1;
    else if (occ.state === 'AWAITING') coverage.awaiting += 1;
    else if (occ.state === 'MISSING') coverage.missing += 1;

    const phase: DerivedStop['phase'] =
      currentSeq === null ? 'FUTURE' : seq < currentSeq ? 'PAST' : seq === currentSeq ? 'CURRENT' : 'FUTURE';

    return {
      seq,
      eva: s.eva ?? null,
      name: s.name ?? s.eva ?? '—',
      cancelled: Boolean(s.cancelled),
      platform: s.platform ?? s.platform_schedule ?? null,
      arrival,
      departure,
      called,
      calledAtMs,
      phase,
      isTerminus: idx === stopDocs.length - 1,
      occupancy: occ,
    };
  });

  const currentStop = stops.find((s) => s.seq === currentSeq) ?? null;
  const nextStop = stops.find((s) => currentSeq !== null && s.seq > currentSeq) ?? stops[0] ?? null;

  return {
    journeyId: journey.journey_id ?? '',
    routeId: journey.route_id ?? null,
    lineCode: journey.line_code ?? 'UNKNOWN',
    status: deriveStatus(journey, nowMs),
    cancelled: Boolean(journey.cancelled),
    originName: journey.origin?.name ?? null,
    destinationName: journey.destination?.name ?? null,
    originSchedLabel: hhmm(journey.origin?.scheduled_departure),
    destinationSchedLabel: hhmm(journey.destination?.scheduled_arrival),
    currentSeq,
    nextSeq: nextStop ? nextStop.seq : null,
    delaySec: currentStop ? currentStop.departure.delaySec ?? currentStop.arrival.delaySec ?? null : null,
    expiresMs: toMs(journey.expires_at),
    sourceUpdatedMs: toMs(journey.source_last_updated),
    stops,
    coverage,
  };
}

/**
 * Is this journey live right now?
 *
 * Upper bound: expires_at, client-enforced by design — if the writers stop, trains
 * still leave the board on schedule and only "no new trains appear" is lost.
 * Lower bound: scheduled departure minus a lead, so the board shows trains running
 * or about to run rather than the whole timetable.
 *
 * A document with no expiry is dropped rather than shown indefinitely. A document
 * with no scheduled departure is kept — it has an expiry, so it will age out, and
 * a journey whose schedule we cannot read is worth seeing.
 */
export function isLive(schedDepMs: number | null, expiresMs: number | null, nowMs: number): boolean {
  if (expiresMs === null) return false;
  if (nowMs >= expiresMs) return false;
  if (schedDepMs !== null && nowMs < schedDepMs - PRE_DEPARTURE_LEAD_MS) return false;
  return true;
}

/**
 * Service days to query, as 'YYYY-MM-DD' in Berlin time.
 *
 * Yesterday is included because a train departing at 23:50 keeps yesterday's
 * date_cal while it is still running after midnight; querying today alone would
 * drop it mid-journey.
 */
export function serviceDays(nowMs: number): string[] {
  const fmt = (ms: number) =>
    new Intl.DateTimeFormat('en-CA', {
      timeZone: 'Europe/Berlin', year: 'numeric', month: '2-digit', day: '2-digit',
    }).format(new Date(ms));
  return [fmt(nowMs - 86400000), fmt(nowMs)];
}

export function lineSortKey(id: string): number {
  const m = /^S(\d+)$/.exec(id.trim().toUpperCase());
  return m ? parseInt(m[1], 10) : 9999;
}
